"use client";

import Image from "next/image";
import { useState, useTransition } from "react";
import type { NewsArticle } from "@/lib/newsclip-api";
import { getPostUrl } from "@/lib/newsclip-url";

type NewsListProps = {
  initialArticles: NewsArticle[];
};

export function NewsList({ initialArticles }: NewsListProps) {
  const [articles, setArticles] = useState(initialArticles);
  const [page, setPage] = useState(2);
  const [hasMore, setHasMore] = useState(initialArticles.length > 0);
  const [isPending, startTransition] = useTransition();

  function loadMore() {
    startTransition(async () => {
      const response = await fetch("/api/news", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          Page: page
        })
      });

      if (!response.ok) {
        setHasMore(false);
        return;
      }

      const nextArticles = (await response.json()) as NewsArticle[];

      if (nextArticles.length === 0) {
        setHasMore(false);
        return;
      }

      setArticles((currentArticles) => [...currentArticles, ...nextArticles]);
      setPage((currentPage) => currentPage + 1);
    });
  }

  return (
    <>
      <div className="news-list">
        {articles.map((article) => (
          <article className="news-card" key={article.UniqueUrl || article.Headline}>
            <a className="news-image" href={getPostUrl(article.UniqueUrl)} aria-label={`View ${article.Headline}`}>
              {article.ImgPath ? (
                <Image src={article.ImgPath} alt={article.Headline} fill sizes="(max-width: 760px) 100vw, 260px" />
              ) : null}
            </a>
            <div className="news-card-body">
              <p className="blog-meta">
                {[article.Category, article.DisplayDate].filter(Boolean).join(" | ")}
              </p>
              <h2>
                <a href={getPostUrl(article.UniqueUrl)}>{article.Headline}</a>
              </h2>
              <p>{article.Synopsis}</p>
              <a className="blog-button" href={getPostUrl(article.UniqueUrl)}>
                View
              </a>
            </div>
          </article>
        ))}
      </div>
      {hasMore ? (
        <div className="news-load-more">
          <button className="button" type="button" onClick={loadMore} disabled={isPending}>
            {isPending ? "Loading..." : "Load More"}
          </button>
        </div>
      ) : null}
    </>
  );
}
